import SessionManager from "./sessionManager";
import { refreshAccessToken, isTokenExpiringSoon, getTokenExpiryTime } from "./tokenRefresh";

class SessionExpiryTimer {
  private static timer: ReturnType<typeof setTimeout> | null = null;
  private static refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private static finalLogoutTimer: ReturnType<typeof setTimeout> | null = null;
  private static showSessionExpiredModal: (() => void) | null = null;
  private static isIdleLogoutInProgress: boolean = false;

  static registerModalCallback(showModal: () => void) {
    this.showSessionExpiredModal = showModal;
  }

  static setIdleLogoutInProgress(value: boolean) {
    this.isIdleLogoutInProgress = value;
  }

  static startSessionExpiry() {
    this.clearTimers();

    const token = localStorage.getItem("access_token");
    if (!token) return;

    const expiryInMs = getTokenExpiryTime(token);
    if (!expiryInMs) return;

    const now = Date.now();
    const remainingMs = expiryInMs - now;
    // const remainingMs = 60000 * 3;
    const REFRESH_BUFFER = 1 * 60 * 1000; // 1 minute before expiry
    const WARNING_THRESHOLD = 30_000; // 30 seconds before expiry

    console.log("SessionExpiryTimer: Token expires in ms:", remainingMs);

    if (remainingMs <= 0) {
      // Token already expired → Try to refresh, if fails then logout
      this.handleTokenExpired();
      return;
    }

    if (remainingMs <= WARNING_THRESHOLD) {
      // Token expiring in ≤ 30 sec → show modal now
      this.triggerWarningAndLogout();
      return;
    }

    // Schedule auto-refresh 5 minutes before expiry
    const timeToRefresh = remainingMs - REFRESH_BUFFER;
    if (timeToRefresh > 0) {
      this.refreshTimer = setTimeout(() => {
        this.attemptSilentRefresh();
      }, timeToRefresh);
    } else {
      // Already within 5 minutes, refresh now
      this.attemptSilentRefresh();
    }

    // Schedule warning exactly 30 sec before expiry (fallback if refresh fails)
    const timeToWarning = remainingMs - WARNING_THRESHOLD;
    this.timer = setTimeout(() => {
      this.triggerWarningAndLogout();
    }, timeToWarning);
  }

  private static async attemptSilentRefresh() {
    // Don't refresh if idle logout is in progress
    if (this.isIdleLogoutInProgress) {
      return;
    }

    // Check if user is active (has activity in last 2 minutes)
    const lastActivity = localStorage.getItem("lastActivity");
    const now = Date.now();
    const twoMinutesAgo = now - 2 * 60 * 1000;

    const isUserActive = lastActivity && parseInt(lastActivity) > twoMinutesAgo;

    if (!isUserActive) {
      // User is inactive, don't refresh - let idle timer or expiry handle it
      console.log("SessionExpiryTimer: User inactive, skipping refresh");
      return;
    }

    // User is active - attempt silent refresh
    console.log("SessionExpiryTimer: Attempting silent token refresh");
    const refreshed = await refreshAccessToken();

    if (refreshed && refreshed.access_token) {
      // Refresh succeeded - restart timer with new token
      console.log("SessionExpiryTimer: Token refreshed successfully");
      this.clearTimers();
      this.startSessionExpiry();
    } else {
      // Refresh failed - user needs to log in again
      console.warn("SessionExpiryTimer: Token refresh failed");
      // Don't show modal here - wait for warning timer or 401 error
    }
  }

  private static async handleTokenExpired() {
    // Token already expired - try refresh if user is active
    const lastActivity = localStorage.getItem("lastActivity");
    const now = Date.now();
    const twoMinutesAgo = now - 2 * 60 * 1000;
    const isUserActive = lastActivity && parseInt(lastActivity) > twoMinutesAgo;

    if (isUserActive && !this.isIdleLogoutInProgress) {
      // User is active, try to refresh
      const refreshed = await refreshAccessToken();
      if (refreshed && refreshed.access_token) {
        // Success - restart timer
        this.clearTimers();
        this.startSessionExpiry();
        return;
      }
    }

    // Refresh failed or user inactive - proceed with logout
    SessionManager.getInstance().handleSessionExpired();
  }

  private static triggerWarningAndLogout() {
    // Don't show modal if idle logout is in progress
    if (this.isIdleLogoutInProgress) {
      return;
    }

    if (this.showSessionExpiredModal) {
      this.showSessionExpiredModal();
    }

    // Check again if user becomes active during countdown
    const checkActivity = setInterval(() => {
      const lastActivity = localStorage.getItem("lastActivity");
      const now = Date.now();
      const twoMinutesAgo = now - 2 * 60 * 1000;
      const isUserActive = lastActivity && parseInt(lastActivity) > twoMinutesAgo;

      if (isUserActive && !this.isIdleLogoutInProgress) {
        // User became active - cancel logout and try refresh
        clearInterval(checkActivity);
        clearTimeout(this.finalLogoutTimer!);

        refreshAccessToken().then((refreshed) => {
          if (refreshed && refreshed.access_token) {
            // Success - restart timer
            this.clearTimers();
            this.startSessionExpiry();
          } else {
            // Failed - show modal again
            if (this.showSessionExpiredModal) {
              this.showSessionExpiredModal();
            }
            this.finalLogoutTimer = setTimeout(() => {
              SessionManager.getInstance().handleSessionExpired();
            }, 30_000);
          }
        });
      }
    }, 5000); // Check every 5 seconds

    this.finalLogoutTimer = setTimeout(() => {
      clearInterval(checkActivity);
      SessionManager.getInstance().handleSessionExpired();
    }, 30_000);
  }

  static clearTimers() {
    if (this.timer) clearTimeout(this.timer);
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (this.finalLogoutTimer) clearTimeout(this.finalLogoutTimer);
    this.timer = null;
    this.refreshTimer = null;
    this.finalLogoutTimer = null;
  }
}

export default SessionExpiryTimer;